import { useEffect, useMemo, useState, type ReactNode } from 'react'
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import BoltRoundedIcon from '@mui/icons-material/BoltRounded'
import ChatBubbleRoundedIcon from '@mui/icons-material/ChatBubbleRounded'
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded'
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded'
import GppGoodRoundedIcon from '@mui/icons-material/GppGoodRounded'
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded'
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded'
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded'
import SaveRoundedIcon from '@mui/icons-material/SaveRounded'
import SettingsSuggestRoundedIcon from '@mui/icons-material/SettingsSuggestRounded'
import SmartphoneRoundedIcon from '@mui/icons-material/SmartphoneRounded'
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded'
import VibrationRoundedIcon from '@mui/icons-material/VibrationRounded'
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded'
import { useAuth } from '../../context/useAuth'
import { useClientPreferences } from '../../context/useClientPreferences'
import {
  HELPER_APP_LOCALE_OPTIONS,
  fetchLanguageRegistry,
  getHelperAppLocalePreference,
  getHelperLanguagePreference,
  normalizeHelperAppLocale,
  setHelperAppLocalePreference,
  setHelperLanguagePreference,
  type LanguageRegistry,
} from '../../services/helper/languageRegistryService'
import { useNls } from '../../nls/nlsProvider'
import { clearAllPersistedState } from '../../hooks/usePersistedState'
import { getMobilePushPermissionState, registerMobilePushToken } from '../../services/mobilePushRegistrationService'
import { Button } from '../Common/Button'
import { Pressable } from '../Common/Pressable'
import { Select, Slider, Toggle } from '../Common'
import { SheetSelect } from '../Common/SheetSelect'
import { AppearancePanel } from '../Settings/AppearancePanel'
import { DevicesPanel } from '../Settings/DevicesPanel'
import { AiMagicPanel } from '../Settings/panels/AiMagicPanel'
import { ProfilePanel } from '../Settings/panels/ProfilePanel'
import { SecurityPanel } from '../Settings/panels/SecurityPanel'
import { NotificationSettings } from '../Settings/NotificationSettings'
import { MemberAccountPanel } from './MemberAccountPanel'
import { setHelperNotifyVia } from '../../services/helperNotifyViaService'

type MobileSettingsRole = 'HELPER' | 'MEMBER'

type MobileSettingsCommandCenterProps = {
  role: MobileSettingsRole
  displayName: string | null
  identity?: string | null
  workspaceId?: string | null
  workspaceName?: string | null
  workspaceRole?: string | null
  workspaceCount?: number
  pendingSyncCount?: number
  failedSyncCount?: number
  unreadCount?: number
  helperAppLocale?: string | null
  notifyVia?: 'BOTH' | 'APP_ONLY' | 'WHATSAPP_ONLY'
  onHelperAppLocaleChange?: (locale: string) => Promise<void> | void
  onOpenProfile?: () => void
  onOpenIncomingInvites?: () => void
  onSignOut: () => void
  initialSection?: 'ALERTS'
}

type MemberLanding = 'TODOS' | 'BOARD' | 'AI' | 'NEXUS' | 'CONTROLS'
type MemberBoardDefault = 'STRIPBOARD' | 'FOLDER' | 'DASHBOARD' | 'CALENDAR' | 'MEETINGS'
type HelperLanding = 'TASKS' | 'CALENDAR' | 'MEETINGS' | 'CHAT' | 'CONTROLS'
type SortMode = 'DUE' | 'PRIORITY' | 'WORKSPACE' | 'UPDATED'
type EvidenceSource = 'ASK' | 'CAMERA' | 'GALLERY' | 'FILES'
type ScanMode = 'OUT' | 'IN'
type ReminderLead = 0 | 5 | 10 | 15 | 30 | 60
const REMINDER_LEADS: ReminderLead[] = [0, 5, 10, 15, 30, 60]

type MobileRolePreferences = {
  landing: MemberLanding | HelperLanding
  todoSort: SortMode
  hideCompleted: boolean
  showBlocked: boolean
  autoOpenNewest: boolean
  allWorkspaces: boolean
  confirmDone: boolean
  confirmProof: boolean
  evidenceSource: EvidenceSource
  keepDetailsOpen: boolean
  defaultScanMode: ScanMode
  requireScanTarget: boolean
  keepScannerOpen: boolean
  showScanMismatchDetails: boolean
  autoTranslateChat: boolean
  autoSummarizeChat: boolean
  taskQuickActions: boolean
  meetingReminderMinutes: ReminderLead
  openLiveMeeting: boolean
  taskLinkedMeetingsFirst: boolean
  boardDefault: MemberBoardDefault
  rememberBoardView: boolean
  openChecklistDetails: boolean
  aiDefault: 'HISTORY' | 'CREATE' | 'EXECUTE' | 'PULSE'
  formsPendingFirst: boolean
  keepSubmittedForms: boolean
  automationConfirm: boolean
  hideUnavailableAutomations: boolean
  auditDefault: 'LIVE' | 'FULL' | 'REPORTS'
  invitePriority: boolean
  wifiOnlyUploads: boolean
  autoRetryQueue: boolean
  showFailedSyncBadge: boolean
  receiptMinutes: number
  guidesEnabled: boolean
}

const DEFAULTS: Record<MobileSettingsRole, MobileRolePreferences> = {
  HELPER: {
    landing: 'TASKS',
    todoSort: 'DUE',
    hideCompleted: true,
    showBlocked: true,
    autoOpenNewest: false,
    allWorkspaces: false,
    confirmDone: true,
    confirmProof: true,
    evidenceSource: 'ASK',
    keepDetailsOpen: true,
    defaultScanMode: 'OUT',
    requireScanTarget: true,
    keepScannerOpen: false,
    showScanMismatchDetails: true,
    autoTranslateChat: false,
    autoSummarizeChat: true,
    taskQuickActions: true,
    meetingReminderMinutes: 15,
    openLiveMeeting: true,
    taskLinkedMeetingsFirst: true,
    boardDefault: 'STRIPBOARD',
    rememberBoardView: true,
    openChecklistDetails: true,
    aiDefault: 'CREATE',
    formsPendingFirst: true,
    keepSubmittedForms: false,
    automationConfirm: true,
    hideUnavailableAutomations: true,
    auditDefault: 'LIVE',
    invitePriority: true,
    wifiOnlyUploads: false,
    autoRetryQueue: true,
    showFailedSyncBadge: true,
    receiptMinutes: 5,
    guidesEnabled: true,
  },
  MEMBER: {
    landing: 'TODOS',
    todoSort: 'DUE',
    hideCompleted: true,
    showBlocked: true,
    autoOpenNewest: false,
    allWorkspaces: false,
    confirmDone: true,
    confirmProof: true,
    evidenceSource: 'ASK',
    keepDetailsOpen: true,
    defaultScanMode: 'OUT',
    requireScanTarget: true,
    keepScannerOpen: false,
    showScanMismatchDetails: true,
    autoTranslateChat: false,
    autoSummarizeChat: false,
    taskQuickActions: true,
    meetingReminderMinutes: 15,
    openLiveMeeting: true,
    taskLinkedMeetingsFirst: true,
    boardDefault: 'STRIPBOARD',
    rememberBoardView: true,
    openChecklistDetails: true,
    aiDefault: 'CREATE',
    formsPendingFirst: true,
    keepSubmittedForms: true,
    automationConfirm: true,
    hideUnavailableAutomations: true,
    auditDefault: 'LIVE',
    invitePriority: true,
    wifiOnlyUploads: false,
    autoRetryQueue: true,
    showFailedSyncBadge: true,
    receiptMinutes: 10,
    guidesEnabled: true,
  },
}

const storageKey = (role: MobileSettingsRole) => `timelining:${role.toLowerCase()}:mobile-settings:v1`
const SCREEN_GUIDES_DISABLED_KEY = 'timelining:screen-guides:disabled'

const parseStored = (role: MobileSettingsRole): MobileRolePreferences => {
  if (typeof window === 'undefined') return DEFAULTS[role]
  try {
    const raw = window.localStorage.getItem(storageKey(role))
    const rawRecord: Record<string, unknown> = raw ? JSON.parse(raw) : {}
    if (rawRecord.aiDefault === 'AGENT') rawRecord.aiDefault = 'CREATE'
    if (role === 'MEMBER' && rawRecord.landing === 'CHAT') rawRecord.landing = 'NEXUS'
    const normalized: MobileRolePreferences = { ...DEFAULTS[role], ...rawRecord }
    if (raw && JSON.stringify(normalized) !== raw) {
      window.localStorage.setItem(storageKey(role), JSON.stringify(normalized))
    }
    return normalized
  } catch {
    return DEFAULTS[role]
  }
}

const persistStored = (role: MobileSettingsRole, next: MobileRolePreferences) => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(storageKey(role), JSON.stringify(next))
    window.localStorage.setItem(SCREEN_GUIDES_DISABLED_KEY, next.guidesEnabled ? 'false' : 'true')
  } catch {
    // Local preferences are best-effort.
  }
}

const Section = ({
  id,
  active,
  title,
  kicker,
  icon,
  children,
}: {
  id: string
  active: boolean
  title: string
  kicker: string
  icon: ReactNode
  children: ReactNode
}) => (
  <section className={`mobileSettingsSection ${active ? 'isActive' : ''}`} id={id}>
    <header className="mobileSettingsSectionHeader">
      <span className="mobileSettingsGlyph">{icon}</span>
      <div>
        <span>{kicker}</span>
        <h3>{title}</h3>
      </div>
    </header>
    <div className="mobileSettingsSectionBody">{children}</div>
  </section>
)

const SettingRow = ({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  children: ReactNode
}) => (
  <div className="mobileSettingsRow">
    <div className="mobileSettingsRowCopy">
      <strong>{title}</strong>
      {description ? <span>{description}</span> : null}
    </div>
    <div className="mobileSettingsRowControl">{children}</div>
  </div>
)

const Metric = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="mobileSettingsMetric">
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
)

const updateGuideFlag = (enabled: boolean) => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(SCREEN_GUIDES_DISABLED_KEY, enabled ? 'false' : 'true')
  } catch {
    // ignore
  }
}

export const MobileSettingsCommandCenter = ({
  role,
  displayName,
  identity,
  workspaceId,
  workspaceName,
  workspaceRole,
  workspaceCount = 0,
  pendingSyncCount = 0,
  failedSyncCount = 0,
  unreadCount = 0,
  helperAppLocale,
  notifyVia,
  onHelperAppLocaleChange,
  onOpenProfile,
  onOpenIncomingInvites,
  onSignOut,
  initialSection,
}: MobileSettingsCommandCenterProps) => {
  const { setLocale, t } = useNls()
  const { currentUser } = useAuth()
  const { resetPreferences } = useClientPreferences()
  const [localPrefs, setLocalPrefs] = useState<MobileRolePreferences>(() => parseStored(role))
  const [registry, setRegistry] = useState<LanguageRegistry | null>(null)
  const [language, setLanguage] = useState(() => role === 'HELPER' ? getHelperAppLocalePreference() : getHelperLanguagePreference())
  const [overrideNotifyVia, setOverrideNotifyVia] = useState<'BOTH' | 'APP_ONLY' | 'WHATSAPP_ONLY' | null>(null)
  const currentNotifyVia = overrideNotifyVia ?? notifyVia ?? 'BOTH'
  const [activeSection, setActiveSection] = useState(() => initialSection === 'ALERTS' ? 'notifications' : 'mobile_app')
  const [status, setStatus] = useState<string | null>(null)
  const [pushPermission, setPushPermission] = useState<string>('checking')

  const handleUpdateNotifyVia = async (mode: 'BOTH' | 'APP_ONLY' | 'WHATSAPP_ONLY') => {
    if (!workspaceId) return
    setOverrideNotifyVia(mode)
    try {
      await setHelperNotifyVia({ workspaceId, notifyVia: mode })
      setStatus(t('NLSHelperNotifyViaSaved'))
    } catch {
      setStatus(t('NLSHelperNotifyViaErrorGeneric'))
    }
  }

  useEffect(() => {
    setLocalPrefs(parseStored(role))
    setLanguage(role === 'HELPER' ? normalizeHelperAppLocale(helperAppLocale ?? getHelperAppLocalePreference()) : getHelperLanguagePreference())
  }, [helperAppLocale, role])

  useEffect(() => {
    persistStored(role, localPrefs)
  }, [localPrefs, role])

  useEffect(() => {
    let cancelled = false
    void fetchLanguageRegistry().then((next) => {
      if (!cancelled) setRegistry(next)
    })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    void getMobilePushPermissionState().then((next) => {
      if (!cancelled) setPushPermission(next)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const sections = useMemo(() => [
    { id: 'mobile_app', label: 'App', icon: <SettingsSuggestRoundedIcon /> },
    { id: 'account', label: 'Account', icon: <AccountCircleRoundedIcon /> },
    { id: 'notifications', label: 'Alerts', icon: <NotificationsActiveRoundedIcon /> },
    { id: 'devices', label: 'Devices', icon: <SmartphoneRoundedIcon /> },
    { id: 'look', label: 'Feel', icon: <VisibilityRoundedIcon /> },
    ...(role === 'HELPER' ? [{ id: 'design_editor', label: 'Design', icon: <DashboardRoundedIcon /> }] : []),
    ...(role === 'MEMBER' ? [{ id: 'ai', label: 'AI', icon: <AutoAwesomeRoundedIcon /> }] : []),
    { id: 'work', label: role === 'HELPER' ? 'Todos' : 'Work', icon: <TaskAltRoundedIcon /> },
    { id: role === 'HELPER' ? 'scan' : 'board', label: role === 'HELPER' ? 'Scan' : 'Board', icon: role === 'HELPER' ? <Inventory2RoundedIcon /> : <DashboardRoundedIcon /> },
    { id: 'chat', label: 'Chat', icon: <ChatBubbleRoundedIcon /> },
    { id: 'privacy', label: 'Privacy', icon: <GppGoodRoundedIcon /> },
  ], [role])

  const patchLocal = (patch: Partial<MobileRolePreferences>) => {
    setLocalPrefs((current) => ({ ...current, ...patch }))
    setStatus('Saved on this device.')
  }

  const setLanguagePreference = async (code: string) => {
    if (role === 'HELPER') {
      const locale = setHelperAppLocalePreference(code)
      setLanguage(locale)
      setLocale(locale)
      try {
        await onHelperAppLocaleChange?.(locale)
        setStatus(t('NLSMobileSettingsHelperAppLanguageSaved'))
      } catch (caught) {
        setStatus(caught instanceof Error ? caught.message : 'Could not save Helper app language.')
      }
      return
    }
    setLanguage(code)
    setHelperLanguagePreference(code)
    setStatus('Language preference saved.')
  }

  const languageOptions = role === 'HELPER'
    ? HELPER_APP_LOCALE_OPTIONS.map((entry) => ({
        value: entry.code,
        label: `${entry.nativeLabel} · ${entry.label}${entry.rtl ? ' · RTL' : ''}`,
      }))
    : (registry?.supportedLanguages ?? [{ code: 'en', label: 'English', rtl: false }]).map((entry) => ({
        value: entry.code,
        label: `${entry.label}${entry.rtl ? ' · RTL' : ''}`,
      }))

  const resetLocal = () => {
    const next = DEFAULTS[role]
    setLocalPrefs(next)
    updateGuideFlag(next.guidesEnabled)
    setStatus('Mobile settings reset.')
  }

  const clearLocalQueues = () => {
    clearAllPersistedState()
    setStatus('Local mobile view state cleared.')
  }

  const enableDeviceAlerts = async () => {
    if (!currentUser?.uid) {
      setStatus('Sign in before enabling device alerts.')
      return
    }
    await registerMobilePushToken(currentUser.uid, role, {
      force: true,
      onPermissionChange: (next) => setPushPermission(next),
    })
    const next = await getMobilePushPermissionState()
    setPushPermission(next)
    setStatus(next === 'granted' ? 'Device alerts are enabled.' : 'Device alerts need OS permission.')
  }

  return (
    <div
      className="mobileSettingsCommandCenter"
      data-mobile-settings-role={role.toLowerCase()}
      data-ui-name={role === 'HELPER' ? 'UI-MPCAP-HSET' : 'UI-MPCAP-MSET'}
    >
      {role === 'HELPER' ? <section className="mobileSettingsHero">
        <div className="mobileSettingsHeroText">
          <span>{role === 'HELPER' ? 'Helper command profile' : 'Member command profile'}</span>
          <h2>{displayName || (role === 'HELPER' ? 'Helper' : 'Member')}</h2>
          <p>{workspaceName ? `${workspaceName}${workspaceRole ? ` · ${workspaceRole}` : ''}` : identity || 'Personal app controls'}</p>
        </div>
        <div className="mobileSettingsHeroGrid">
          <Metric label="Workspaces" value={workspaceCount || 0} />
          <Metric label="Unread" value={unreadCount} />
          <Metric label="Queued" value={pendingSyncCount} />
          <Metric label="Failed" value={failedSyncCount} />
        </div>
      </section> : null}

      <p className="mobileSettingsStatus" role="status">
        {status ?? t('NLSMobileSettingsSaveNote')}
      </p>

      <div className="mobileSettingsStack">
        <Section id="mobile_app" active={activeSection === 'mobile_app'} kicker="Mobile only" title="App defaults & local queue" icon={<SettingsSuggestRoundedIcon />}>
          <SettingRow title="Default landing" description="Choose where this mobile app should feel centered when opened.">
            <SheetSelect
              ariaLabel="Default landing"
              value={localPrefs.landing as string}
              options={(role === 'HELPER'
                ? [
                    { value: 'TASKS', label: 'Todos' },
                    { value: 'CALENDAR', label: 'Calendar' },
                    { value: 'MEETINGS', label: 'Meetings' },
                    { value: 'CHAT', label: 'Chat' },
                    { value: 'CONTROLS', label: 'Controls' },
                  ]
                : [
                    { value: 'TODOS', label: 'Todos' },
                    { value: 'BOARD', label: 'Board' },
                    { value: 'AI', label: 'AI Magic' },
                    { value: 'NEXUS', label: 'Nexus' },
                    { value: 'CONTROLS', label: 'Controls' },
                  ])}
              onChange={(value) => patchLocal({ landing: value as MobileRolePreferences['landing'] })}
            />
          </SettingRow>
          <SettingRow
            title={role === 'HELPER' ? t('NLSMobileSettingsHelperAppLanguageTitle') : 'Language'}
            description={role === 'HELPER' ? t('NLSMobileSettingsHelperAppLanguageBody') : 'Used for mobile translation defaults.'}
          >
            <Select
              ariaLabel={role === 'HELPER' ? t('NLSMobileSettingsHelperAppLanguageTitle') : 'Mobile language'}
              value={language}
              searchable
              trigger="pill"
              options={languageOptions}
              onChange={(value) => void setLanguagePreference(value)}
            />
          </SettingRow>
          <SettingRow title="Default evidence source" description="Choose the first source shown for proof.">
            <SheetSelect<EvidenceSource>
              ariaLabel="Evidence source"
              value={localPrefs.evidenceSource}
              options={[
                { value: 'ASK', label: 'Ask every time' },
                { value: 'CAMERA', label: 'Camera' },
                { value: 'GALLERY', label: 'Gallery' },
                { value: 'FILES', label: 'Files' },
              ]}
              onChange={(value) => patchLocal({ evidenceSource: value })}
            />
          </SettingRow>
          {role === 'HELPER' ? (
            <SettingRow title="Default scan direction" description="Pick the first action used by the scanner.">
              <SheetSelect<ScanMode> ariaLabel="Scan direction" value={localPrefs.defaultScanMode} options={[{ value: 'OUT', label: 'Scan out' }, { value: 'IN', label: 'Scan in' }]} onChange={(value) => patchLocal({ defaultScanMode: value })} />
            </SettingRow>
          ) : null}
          {role === 'HELPER' ? <SettingRow title="Screen guides" description="Show the local guide carousel on mobile screens."><Toggle checked={localPrefs.guidesEnabled} onChange={(value) => { updateGuideFlag(value); patchLocal({ guidesEnabled: value }) }} /></SettingRow> : null}
          {role === 'HELPER' ? <div className="mobileSettingsSyncGrid" aria-label="Sync & local queue">
            <Metric label="Pending" value={pendingSyncCount} />
            <Metric label="Failed" value={failedSyncCount} />
            <Metric label="Receipts" value={`${localPrefs.receiptMinutes}m`} />
          </div> : null}
          <SettingRow title="Wi-Fi only uploads" description="Hold proof files until Wi-Fi is available."><Toggle checked={localPrefs.wifiOnlyUploads} onChange={(value) => patchLocal({ wifiOnlyUploads: value })} /></SettingRow>
          <SettingRow title="Auto-retry queue" description="Retry failed work when connectivity returns."><Toggle checked={localPrefs.autoRetryQueue} onChange={(value) => patchLocal({ autoRetryQueue: value })} /></SettingRow>
          <SettingRow title="Failed sync badge" description="Show a persistent badge for failed operations."><Toggle checked={localPrefs.showFailedSyncBadge} onChange={(value) => patchLocal({ showFailedSyncBadge: value })} /></SettingRow>
          <SettingRow title="Completed receipt window" description="How long successful sync receipts stay visible.">
            <div className="mobileSettingsSlider">
              <Slider min={1} max={30} step={1} value={localPrefs.receiptMinutes} onChange={(value) => patchLocal({ receiptMinutes: value })} aria-label="Completed receipt window" />
              <span>{localPrefs.receiptMinutes} min</span>
            </div>
          </SettingRow>
        </Section>

        <Section id="account" active={activeSection === 'account'} kicker="Identity" title="Account & profile" icon={<AccountCircleRoundedIcon />}>
          {role === 'MEMBER' ? <MemberAccountPanel user={currentUser} onSignOut={onSignOut} /> : <ProfilePanel variant="helper" />}
          <div className="mobileSettingsActionGrid">
            {onOpenProfile ? <Button variant="secondary" size="sm" icon={<AccountCircleRoundedIcon />} onClick={onOpenProfile}>Open profile</Button> : null}
            {onOpenIncomingInvites ? <Button variant="secondary" size="sm" icon={<BoltRoundedIcon />} onClick={onOpenIncomingInvites}>Incoming invites</Button> : null}
          </div>
        </Section>

        <Section id="notifications" active={activeSection === 'notifications'} kicker="Delivery" title="Notifications & quiet hours" icon={<NotificationsActiveRoundedIcon />}>
          <div className="mobileSettingsDeviceAlert">
            <div>
              <strong>Android home alerts</strong>
              {pushPermission === 'granted' ? (
                <span>{t('NLSMobileSettingsAlertsOn')}</span>
              ) : (
                <span>{t('NLSMobileSettingsPermissionStatus', { permission: pushPermission })}</span>
              )}
            </div>
            {pushPermission !== 'granted' ? (
              <Button variant="secondary" size="sm" icon={<NotificationsActiveRoundedIcon />} onClick={() => void enableDeviceAlerts()}>
                {t('NLSMobileSettingsEnableAlerts')}
              </Button>
            ) : null}
          </div>
          {role === 'HELPER' && workspaceId ? (
            <div className="mobileSettingsDeviceAlert" data-testid="helper-notify-via-card">
              <div>
                <strong>{t('NLSHelperNotifyViaLabel')}</strong>
                <span>
                  {currentNotifyVia === 'APP_ONLY'
                    ? t('NLSHelperNotifyViaAppOnly')
                    : currentNotifyVia === 'WHATSAPP_ONLY'
                      ? t('NLSHelperNotifyViaWhatsappOnly')
                      : t('NLSHelperNotifyViaBoth')}
                </span>
              </div>
              <div className="flex gap-2">
                <Button
                  variant={currentNotifyVia === 'BOTH' ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => void handleUpdateNotifyVia('BOTH')}
                >
                  {t('NLSHelperNotifyViaBoth')}
                </Button>
                <Button
                  variant={currentNotifyVia === 'APP_ONLY' ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => void handleUpdateNotifyVia('APP_ONLY')}
                >
                  {t('NLSHelperNotifyViaAppOnly')}
                </Button>
                <Button
                  variant={currentNotifyVia === 'WHATSAPP_ONLY' ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => void handleUpdateNotifyVia('WHATSAPP_ONLY')}
                >
                  {t('NLSHelperNotifyViaWhatsappOnly')}
                </Button>
              </div>
            </div>
          ) : null}
          <NotificationSettings memberMobile={role === 'MEMBER'} />
        </Section>

        <Section id="devices" active={activeSection === 'devices'} kicker="Install" title="Devices & app updates" icon={<SmartphoneRoundedIcon />}>
          <DevicesPanel memberMobile={role === 'MEMBER'} />
        </Section>

        <Section id="look" active={activeSection === 'look'} kicker="Sensory layer" title="Appearance, motion & audio" icon={<VisibilityRoundedIcon />}>
          <AppearancePanel memberMobile={role === 'MEMBER'} />
        </Section>

        {role === 'HELPER' ? <Section id="design_editor" active={activeSection === 'design_editor'} kicker="Desktop only" title="Design Editor preferences" icon={<DashboardRoundedIcon />}>
          <article className="mobileSettingsDeviceAlert">
            <div>
              <strong>Design Editor prefs live in desktop Settings</strong>
              <span>Use the desktop console for grid snapping, canvas rulers, export defaults, and component library controls.</span>
            </div>
          </article>
        </Section> : null}

        <Section id="work" active={activeSection === 'work'} kicker={role === 'HELPER' ? 'Field execution' : 'Workspace work'} title="Todos & task flow" icon={<TaskAltRoundedIcon />}>
          <SettingRow title="Todo sort" description="Controls the default order for assigned work feeds.">
            <SheetSelect<SortMode>
              ariaLabel="Todo sort"
              value={localPrefs.todoSort}
              options={[
                { value: 'DUE', label: 'Due first' },
                { value: 'PRIORITY', label: 'Priority first' },
                { value: 'WORKSPACE', label: 'Workspace grouped' },
                { value: 'UPDATED', label: 'Recently updated' },
              ]}
              onChange={(value) => patchLocal({ todoSort: value })}
            />
          </SettingRow>
          <SettingRow title="Hide completed" description="Keep the feed focused on active work."><Toggle checked={localPrefs.hideCompleted} onChange={(value) => patchLocal({ hideCompleted: value })} /></SettingRow>
          <SettingRow title="Show blocked work" description="Blocked items stay visible with a muted treatment."><Toggle checked={localPrefs.showBlocked} onChange={(value) => patchLocal({ showBlocked: value })} /></SettingRow>
          <SettingRow title="Auto-open newest assignment" description="Jump into a fresh assignment when it lands."><Toggle checked={localPrefs.autoOpenNewest} onChange={(value) => patchLocal({ autoOpenNewest: value })} /></SettingRow>
          <SettingRow title="All workspace feed" description="Show assigned work across every joined workspace."><Toggle checked={localPrefs.allWorkspaces} onChange={(value) => patchLocal({ allWorkspaces: value })} /></SettingRow>
          <SettingRow title="Confirm Done" description="Require a second tap before completing work."><Toggle checked={localPrefs.confirmDone} onChange={(value) => patchLocal({ confirmDone: value })} /></SettingRow>
          <SettingRow title="Confirm proof upload" description="Avoid accidental evidence submissions."><Toggle checked={localPrefs.confirmProof} onChange={(value) => patchLocal({ confirmProof: value })} /></SettingRow>
          {role === 'MEMBER' ? <>
            <SettingRow title="Pending forms first" description="Put assigned forms at the top of Forms controls."><Toggle checked={localPrefs.formsPendingFirst} onChange={(value) => patchLocal({ formsPendingFirst: value })} /></SettingRow>
            <SettingRow title="Keep submitted forms" description="Show completed form requests in your mobile queue."><Toggle checked={localPrefs.keepSubmittedForms} onChange={(value) => patchLocal({ keepSubmittedForms: value })} /></SettingRow>
          </> : null}
        </Section>

        {role === 'HELPER' ? (
          <Section id="scan" active={activeSection === 'scan'} kicker="Inventory" title="Scan & proof controls" icon={<Inventory2RoundedIcon />}>
            <SettingRow title="Require selected task" description="Prevent workspace-level scans unless a task is focused."><Toggle checked={localPrefs.requireScanTarget} onChange={(value) => patchLocal({ requireScanTarget: value })} /></SettingRow>
            <SettingRow title="Keep scanner open" description="Useful for repeated material handoffs."><Toggle checked={localPrefs.keepScannerOpen} onChange={(value) => patchLocal({ keepScannerOpen: value })} /></SettingRow>
            <SettingRow title="Show mismatch details" description="Explain SKU/resource mismatches instead of a short error."><Toggle checked={localPrefs.showScanMismatchDetails} onChange={(value) => patchLocal({ showScanMismatchDetails: value })} /></SettingRow>
            <SettingRow title="Keep details open" description="Stay in the task sheet after proof, comment, or status updates."><Toggle checked={localPrefs.keepDetailsOpen} onChange={(value) => patchLocal({ keepDetailsOpen: value })} /></SettingRow>
          </Section>
        ) : (
          <Section id="board" active={activeSection === 'board'} kicker="Workspace map" title="Board, calendar & meetings" icon={<DashboardRoundedIcon />}>
            <SettingRow title="Default board view" description="Pick the board subtab that opens first on mobile.">
              <SheetSelect<MemberBoardDefault>
                ariaLabel="Default board view"
                value={localPrefs.boardDefault}
                options={[
                  { value: 'STRIPBOARD', label: 'Stripboard' },
                  { value: 'FOLDER', label: 'Folder' },
                  { value: 'DASHBOARD', label: 'Dashboard' },
                  { value: 'CALENDAR', label: 'Calendar' },
                  { value: 'MEETINGS', label: 'Meetings' },
                ]}
                onChange={(value) => patchLocal({ boardDefault: value })}
              />
            </SettingRow>
            <SettingRow title="Remember board view" description="Keep the last board subtab between visits."><Toggle checked={localPrefs.rememberBoardView} onChange={(value) => patchLocal({ rememberBoardView: value })} /></SettingRow>
            <SettingRow title="Open checklist details" description="Tap checklist cards into the detail sheet by default."><Toggle checked={localPrefs.openChecklistDetails} onChange={(value) => patchLocal({ openChecklistDetails: value })} /></SettingRow>
            <SettingRow title="Meeting reminder" description="Lead time for workspace meeting nudges.">
              <SheetSelect
                ariaLabel="Meeting reminder"
                value={String(localPrefs.meetingReminderMinutes)}
                options={REMINDER_LEADS.map((lead) => ({
                  value: String(lead),
                  label: lead === 0 ? 'Off' : `${lead} min`,
                }))}
                onChange={(value) => {
                  const lead = REMINDER_LEADS.find((candidate) => String(candidate) === value) ?? 0
                  patchLocal({ meetingReminderMinutes: lead })
                }}
              />
            </SettingRow>
            <SettingRow title="Task-linked meetings first" description="Prioritize meetings tied to assigned work."><Toggle checked={localPrefs.taskLinkedMeetingsFirst} onChange={(value) => patchLocal({ taskLinkedMeetingsFirst: value })} /></SettingRow>
            <SettingRow title="Auto-open live meeting" description="Surface the live meeting card when it starts."><Toggle checked={localPrefs.openLiveMeeting} onChange={(value) => patchLocal({ openLiveMeeting: value })} /></SettingRow>
          </Section>
        )}

        <Section id="chat" active={activeSection === 'chat'} kicker="Conversation" title="Chat actions & translation" icon={<ChatBubbleRoundedIcon />}>
          <SettingRow title="Auto-translate messages" description="Translate incoming messages into your selected language."><Toggle checked={localPrefs.autoTranslateChat} onChange={(value) => patchLocal({ autoTranslateChat: value })} /></SettingRow>
          <SettingRow title="Channel summaries" description="Show AI summaries where the channel allows it."><Toggle checked={localPrefs.autoSummarizeChat} onChange={(value) => patchLocal({ autoSummarizeChat: value })} /></SettingRow>
          <SettingRow title="Task quick actions" description="Enable Evidence, Scan, Done, and Comment from chat."><Toggle checked={localPrefs.taskQuickActions} onChange={(value) => patchLocal({ taskQuickActions: value })} /></SettingRow>
        </Section>

        {role === 'MEMBER' ? (
          <Section id="ai" active={activeSection === 'ai'} kicker="Control plane" title="AI, forms & automations" icon={<AutoAwesomeRoundedIcon />}>
            <AiMagicPanel />
            <SettingRow title="Default AI tab" description="Choose which AI Magic section appears first.">
              <SheetSelect ariaLabel="Default AI tab" value={localPrefs.aiDefault} options={[{ value: 'CREATE', label: 'Create' }, { value: 'EXECUTE', label: 'Execute' }, { value: 'HISTORY', label: 'History' }, { value: 'PULSE', label: 'Pulse' }]} onChange={(value) => patchLocal({ aiDefault: value })} />
            </SettingRow>
            <SettingRow title="Confirm automations" description="Ask before running manual automation starters."><Toggle checked={localPrefs.automationConfirm} onChange={(value) => patchLocal({ automationConfirm: value })} /></SettingRow>
            <SettingRow title="Hide unavailable starters" description="Keep locked or unsupported automations out of the launcher."><Toggle checked={localPrefs.hideUnavailableAutomations} onChange={(value) => patchLocal({ hideUnavailableAutomations: value })} /></SettingRow>
            <SettingRow title="Audit default" description="For Admin/Editor members who can open Audit.">
              <SheetSelect ariaLabel="Audit default" value={localPrefs.auditDefault} options={[{ value: 'LIVE', label: 'Live ledger' }, { value: 'FULL', label: 'Full logs' }, { value: 'REPORTS', label: 'Reports' }]} onChange={(value) => patchLocal({ auditDefault: value })} />
            </SettingRow>
            <SettingRow title="Invite priority" description="Treat incoming invites as high-priority mobile signals."><Toggle checked={localPrefs.invitePriority} onChange={(value) => patchLocal({ invitePriority: value })} /></SettingRow>
          </Section>
        ) : null}

        <Section id="privacy" active={activeSection === 'privacy'} kicker="Device" title="Privacy, cache & diagnostics" icon={<GppGoodRoundedIcon />}>
          <SecurityPanel />
          <div className="mobileSettingsActionGrid">
            <Button variant="secondary" size="sm" icon={<RestartAltRoundedIcon />} onClick={resetLocal}>Reset mobile settings</Button>
            <Button variant="tertiary" size="sm" icon={<FactCheckRoundedIcon />} onClick={clearLocalQueues}>Clear view cache</Button>
            <Button variant="tertiary" size="sm" icon={<VibrationRoundedIcon />} onClick={resetPreferences}>Reset experience</Button>
          </div>
          <Button variant="secondary" size="sm" icon={<SaveRoundedIcon />} fullWidth onClick={onSignOut}>Sign out</Button>
        </Section>
      </div>

      <nav className="mobileSettingsRail" aria-label={`${role.toLowerCase()} settings sections`}>
        {sections.map((section) => (
          <Pressable
            key={section.id}
            type="button"
            aria-pressed={activeSection === section.id}
            className={activeSection === section.id ? 'isActive' : ''}
            onClick={() => setActiveSection(section.id)}
          >
            {section.icon}
            <span>{section.label}</span>
          </Pressable>
        ))}
      </nav>
    </div>
  )
}
